import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { storybookAPI } from '../utils/api';
import { Storybook } from '../types/storybook';
import StorybookWizard from '../components/StorybookWizard';
import { QUICK_CREATE_STAGE_LABELS } from '../utils/quickCreate';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [storybooks, setStorybooks] = useState<Storybook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyingStorybook, setCopyingStorybook] = useState<Storybook | null>(null);
  const [newCanvasSize, setNewCanvasSize] = useState('6x9');
  const [newTitle, setNewTitle] = useState('');
  const [copying, setCopying] = useState(false);

  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelections, setMergeSelections] = useState<string[]>([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTitle, setMergeTitle] = useState('');
  const [mergeCanvasSize, setMergeCanvasSize] = useState('6x9');
  const [mergeRemoveCovers, setMergeRemoveCovers] = useState(true);
  const [merging, setMerging] = useState(false);

  const CANVAS_SIZES = [
    // Paperback Trim Sizes
    { value: '5x8', label: '5" × 8" (Paperback)' },
    { value: '5.06x7.81', label: '5.06" × 7.81" (Paperback)' },
    { value: '5.25x8', label: '5.25" × 8" (Paperback)' },
    { value: '5.5x8.5', label: '5.5" × 8.5" (Paperback)' },
    { value: '6x9', label: '6" × 9" (Paperback)' },
    
    // Large Paperback Trim Sizes
    { value: '6.14x9.21', label: '6.14" × 9.21" (Paperback)' },
    { value: '6.69x9.61', label: '6.69" × 9.61" (Paperback)' },
    { value: '7x10', label: '7" × 10" (Paperback)' },
    { value: '7.44x9.69', label: '7.44" × 9.69" (Paperback)' },
    { value: '7.5x9.25', label: '7.5" × 9.25" (Paperback)' },
    { value: '8x10', label: '8" × 10" (Paperback)' },
    { value: '8.25x6', label: '8.25" × 6" (Paperback)' },
    { value: '8.25x8.25', label: '8.25" × 8.25" (Paperback)' },
    { value: '8.5x8.5', label: '8.5" × 8.5" (Paperback)' },
    { value: '8.5x11', label: '8.5" × 11" (Paperback)' },
    { value: '8.27x11.69', label: '8.27" × 11.69" (Paperback)' },
    
    // Hardcover Trim Sizes
    { value: '5.5x8.5-hc', label: '5.5" × 8.5" (Hardcover)' },
    { value: '6x9-hc', label: '6" × 9" (Hardcover)' },
    { value: '6.14x9.21-hc', label: '6.14" × 9.21" (Hardcover)' },
    { value: '7x10-hc', label: '7" × 10" (Hardcover)' },
    { value: '8.25x11-hc', label: '8.25" × 11" (Hardcover)' }
  ];

  useEffect(() => {
    loadStorybooks();
  }, []);

  const loadStorybooks = async () => {
    try {
      const response = await storybookAPI.getAll();
      setStorybooks(response.storybooks);
    } catch (error) {
      console.error('Failed to load storybooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWizardComplete = (storybookId: string) => {
    setShowWizard(false);
    loadStorybooks();
  };

  const handleDeleteStorybook = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this storybook?')) {
      return;
    }

    try {
      await storybookAPI.delete(id);
      setStorybooks(storybooks.filter(s => s._id !== id));
    } catch (error) {
      console.error('Failed to delete storybook:', error);
    }
  };

  const handleCopyStorybook = (storybook: Storybook) => {
    setCopyingStorybook(storybook);
    setNewTitle(`${storybook.title} (Copy)`);
    setNewCanvasSize(storybook.canvasSize || '6x9');
    setShowCopyModal(true);
  };

  const handleToggleMergeSelect = (id: string) => {
    setMergeSelections(prev => {
      if (prev.includes(id)) return prev.filter(s => s !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const handleOpenMergeModal = () => {
    if (mergeSelections.length !== 2) return;
    const book1 = storybooks.find(s => s._id === mergeSelections[0]);
    const book2 = storybooks.find(s => s._id === mergeSelections[1]);
    if (!book1 || !book2) return;
    setMergeTitle(`${book1.title} + ${book2.title}`);
    setMergeCanvasSize(book1.canvasSize || '6x9');
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (mergeSelections.length !== 2) return;
    setMerging(true);
    try {
      const response = await storybookAPI.merge({
        storybookId1: mergeSelections[0],
        storybookId2: mergeSelections[1],
        title: mergeTitle,
        canvasSize: mergeCanvasSize,
        removeDuplicateCovers: mergeRemoveCovers
      });
      await loadStorybooks();
      setShowMergeModal(false);
      setMergeMode(false);
      setMergeSelections([]);
      if (response.storybook?._id) {
        navigate(`/editor/${response.storybook._id}`);
      }
    } catch (error) {
      console.error('Failed to merge storybooks:', error);
      alert('Failed to merge storybooks. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  const handleConfirmCopy = async () => {
    if (!copyingStorybook) return;

    setCopying(true);
    try {
      const response = await storybookAPI.copy(copyingStorybook._id, {
        canvasSize: newCanvasSize,
        title: newTitle
      });
      
      // Reload storybooks to show the new copy
      await loadStorybooks();
      
      setShowCopyModal(false);
      setCopyingStorybook(null);
      
      // Optionally navigate to the new storybook
      if (response.storybook?._id) {
        navigate(`/editor/${response.storybook._id}`);
      }
    } catch (error) {
      console.error('Failed to copy storybook:', error);
      alert('Failed to copy storybook. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1 onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/banner-icon.png" alt="Tiny Little Fox" className="banner-icon" />
          Tiny Little Fox
        </h1>
        <div className="header-actions">
          <span className="user-info">
            {user?.name} {user?.hasApiAccess && <span className="api-badge">✨ AI Access</span>}
            {user?.isAdmin && <span className="admin-badge">👑 Admin</span>}
          </span>
          {user?.isAdmin && (
            <button className="btn btn-warning" onClick={() => navigate('/admin')}>
              🛠️ Admin Panel
            </button>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/settings')}>⚙️ Settings</button>
          <button className="btn btn-secondary" onClick={logout}>Logout</button>
        </div>
      </header>

      <div className="dashboard-content">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '32px' }}>
          <button className="btn btn-primary" style={{ fontSize: '18px', padding: '12px 24px' }} onClick={() => setShowWizard(true)}>
            + Create New Storybook
          </button>
          {!mergeMode ? (
            <button
              className="btn btn-outline"
              style={{ fontSize: '18px', padding: '12px 24px' }}
              onClick={() => { setMergeMode(true); setMergeSelections([]); }}
              disabled={storybooks.length < 2}
              title={storybooks.length < 2 ? 'Need at least 2 storybooks to merge' : 'Select two storybooks to merge'}
            >
              🔀 Merge Books
            </button>
          ) : (
            <>
              <span style={{ fontSize: '14px', color: '#6B7280' }}>
                Select 2 storybooks to merge ({mergeSelections.length}/2)
              </span>
              <button
                className="btn btn-primary"
                onClick={handleOpenMergeModal}
                disabled={mergeSelections.length !== 2}
              >
                Merge Selected
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setMergeMode(false); setMergeSelections([]); }}
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {loading ? (
          <div className="spinner"></div>
        ) : storybooks.length === 0 ? (
          <div className="empty-state">
            <p>No storybooks yet. Create your first one!</p>
          </div>
        ) : (
          <div className="storybooks-grid">
            {storybooks.map(storybook => {
              const firstPageThumbnail = storybook.pages?.[0]?.thumbnail;
              const pageCount = storybook.pages?.length || 0;
              const characterCount = storybook.characters?.length || 0;
              
              return (
                <div
                  key={storybook._id}
                  className={`storybook-card${mergeMode && mergeSelections.includes(storybook._id) ? ' storybook-card-selected' : ''}`}
                  onClick={mergeMode ? (e) => { e.stopPropagation(); handleToggleMergeSelect(storybook._id); } : undefined}
                  style={mergeMode ? { cursor: 'pointer', position: 'relative' } : undefined}
                >
                  {mergeMode && (
                    <div style={{
                      position: 'absolute', top: '8px', left: '8px', zIndex: 10,
                      width: '28px', height: '28px', borderRadius: '50%',
                      background: mergeSelections.includes(storybook._id) ? '#4F46E5' : '#E5E7EB',
                      color: mergeSelections.includes(storybook._id) ? '#fff' : '#9CA3AF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold', fontSize: '14px', border: '2px solid #fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }}>
                      {mergeSelections.includes(storybook._id)
                        ? mergeSelections.indexOf(storybook._id) + 1
                        : ''}
                    </div>
                  )}
                  {firstPageThumbnail && (
                    <div className="storybook-thumbnail">
                      <img src={firstPageThumbnail} alt={storybook.title} />
                    </div>
                  )}
                  <div className="storybook-content" onClick={() => navigate(`/editor/${storybook._id}`)}>
                    <h3>{storybook.title}</h3>
                    <p className="storybook-description">{storybook.description || 'No description'}</p>
                    <div className="storybook-meta">
                      <span>📄 {pageCount} {pageCount === 1 ? 'page' : 'pages'}</span>
                      <span>👤 {characterCount} {characterCount === 1 ? 'character' : 'characters'}</span>
                    </div>
                    <div className="storybook-date">
                      Updated: {new Date(storybook.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="storybook-actions">
                    {storybook.quickCreate && storybook.quickCreate.status !== 'completed' && (
                      <button
                        className="btn btn-primary quick-create-resume"
                        data-testid="resume-quick-create"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/editor/${storybook._id}?quickCreate=true`);
                        }}
                      >
                        Continue · {QUICK_CREATE_STAGE_LABELS[storybook.quickCreate.stage]}
                      </button>
                    )}
                    <button 
                      className="btn btn-outline"
                      onClick={() => navigate(`/editor/${storybook._id}`)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-info"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyStorybook(storybook);
                      }}
                    >
                      Copy
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => handleDeleteStorybook(storybook._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showWizard && (
        <StorybookWizard 
          onClose={() => setShowWizard(false)} 
          onComplete={handleWizardComplete}
        />
      )}

      {showMergeModal && mergeSelections.length === 2 && (
        <div className="modal-overlay" onClick={() => !merging && setShowMergeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🔀 Merge Storybooks</h2>
            <p>
              Merge <strong>"{storybooks.find(s => s._id === mergeSelections[0])?.title}"</strong> and{' '}
              <strong>"{storybooks.find(s => s._id === mergeSelections[1])?.title}"</strong> into a new storybook.
            </p>

            <div className="modal-info" style={{ marginBottom: '16px' }}>
              <p><strong>What gets merged:</strong></p>
              <ul style={{ fontSize: '13px', margin: '8px 0', paddingLeft: '20px', color: '#6B7280' }}>
                <li>All pages from Book 1, then all pages from Book 2</li>
                <li>Characters, assets, backgrounds &amp; scenes (deduplicated by name)</li>
                <li>Style settings from the first book</li>
              </ul>
            </div>

            <div className="form-group">
              <label>Merged Title:</label>
              <input
                type="text"
                value={mergeTitle}
                onChange={(e) => setMergeTitle(e.target.value)}
                placeholder="Enter title for merged storybook"
                disabled={merging}
              />
            </div>

            <div className="form-group">
              <label>Canvas Size:</label>
              <select
                value={mergeCanvasSize}
                onChange={(e) => setMergeCanvasSize(e.target.value)}
                disabled={merging}
              >
                {CANVAS_SIZES.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="mergeRemoveCovers"
                  checked={mergeRemoveCovers}
                  onChange={(e) => setMergeRemoveCovers(e.target.checked)}
                  disabled={merging}
                  style={{ width: '16px', height: '16px' }}
                />
                <label htmlFor="mergeRemoveCovers" style={{ fontSize: '14px', cursor: 'pointer' }}>
                  Remove duplicate cover pages from Book 2
                </label>
              </div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '4px 0 0 24px' }}>
                When checked, the Title Cover and Back Cover from the second book will be removed to avoid duplicates.
              </p>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowMergeModal(false)}
                disabled={merging}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmMerge}
                disabled={merging || !mergeTitle.trim()}
              >
                {merging ? 'Merging...' : '🔀 Merge & Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyModal && copyingStorybook && (
        <div className="modal-overlay" onClick={() => !copying && setShowCopyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Copy Storybook</h2>
            <p>Create a copy of "{copyingStorybook.title}" with a new format</p>
            
            <div className="form-group">
              <label>New Title:</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter new title"
                disabled={copying}
              />
            </div>

            <div className="form-group">
              <label>Canvas Size:</label>
              <select
                value={newCanvasSize}
                onChange={(e) => setNewCanvasSize(e.target.value)}
                disabled={copying}
              >
                {CANVAS_SIZES.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-info">
              <p><strong>Note:</strong> The copy will maintain all content (text, images, characters) from the original storybook but with the new canvas size format.</p>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowCopyModal(false)}
                disabled={copying}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmCopy}
                disabled={copying || !newTitle.trim()}
              >
                {copying ? 'Copying...' : 'Create Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
